import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { makeStyles } from 'tss-react/mui';

import { ChatTranslateBold } from '@ha/icons';
import { Typography, Button } from '@hbf/dsl';
import { LongParagraph } from 'ha/ui.legacy/LongParagraph';
import { SmartTrack as Track } from 'ha/components/track/SmartTrack';
import { useIntl } from 'ha/i18n';
import { send as showAlert } from 'ha/modules/Alertbar/actions';
import { getCurrentLanguage } from 'ha/modules/AuthLogic/selectors';
import { useUnitTypeId } from 'ha/modules/MultiUnitListings/hooks';

import {
  detectLanguage,
  translateDescription,
  toggleLanguage,
} from '../actions';
import {
  getDescription,
  getDescriptionLanguage,
  getHasTranslation,
  getIsTranslationLoading,
  getIsTranslationShown,
  getListingId,
  getIsMultiUnit,
  getUnitTypeUnits,
} from '../selectors';

const useStyles = makeStyles()(theme => ({
  button: {
    backgroundColor: theme.palette.grey['200'],
    color: theme.palette.text.primary,
    '&:hover': {
      backgroundColor: theme.palette.grey['300'],
    },
  },
  caption: {
    marginTop: theme.spacing(1),
  },
}));

const ListingDescription: React.FC = () => {
  const { T } = useIntl();
  const { classes } = useStyles();
  const dispatch = useDispatch();

  const { isUnitTypeId } = useUnitTypeId();

  const description = useSelector(getDescription);
  const hasTranslation = useSelector(getHasTranslation);
  const isLoading = useSelector(getIsTranslationLoading);
  const isTranslationShown = useSelector(getIsTranslationShown);
  const listingId = useSelector(getListingId);
  const userLang = useSelector(getCurrentLanguage);
  const descriptionLang = useSelector(getDescriptionLanguage);
  const isMultiUnit = useSelector(getIsMultiUnit);
  const units = useSelector(getUnitTypeUnits);

  const handleTranslateBtnClick = React.useCallback(async () => {
    if (hasTranslation) {
      dispatch(toggleLanguage());
    } else {
      try {
        await dispatch(
          translateDescription({
            listingId: isUnitTypeId ? units?.[0]?.id : listingId,
            targetLang: userLang,
          }),
        );
      } catch {
        dispatch(
          showAlert({
            text: T('Error has occurred while translating description'),
            kind: 'danger',
            dismissAfter: 10000,
          }),
        );
      }
    }
  }, [T, hasTranslation, listingId, userLang, dispatch, isUnitTypeId, units]);

  React.useEffect(() => {
    dispatch(detectLanguage(description));
  }, [description, dispatch]);

  const analyticsEventProps = React.useMemo(() => {
    return isUnitTypeId
      ? {
          unitId: isMultiUnit ? undefined : units?.[0]?.id,
          unitTypeId: listingId,
          isMultiUnit,
        }
      : {};
  }, [isUnitTypeId, isMultiUnit, listingId, units]);

  const roomId = React.useMemo(() => {
    if (isUnitTypeId) {
      return isMultiUnit ? undefined : units?.[0]?.id;
    }
    return listingId;
  }, [isUnitTypeId, isMultiUnit, listingId, units]);

  return (
    <div data-test-locator="Listing/ListingDescription">
      <Track name="Clicked Show more/less description" type="button">
        <LongParagraph preWrap>{description}</LongParagraph>
      </Track>
      {descriptionLang && descriptionLang !== userLang && (
        <React.Fragment>
          <Track
            name="Clicked Translate description"
            type="button"
            roomID={roomId}
            getDynamicData={() => analyticsEventProps}
          >
            <Button
              variant="contained"
              className={classes.button}
              onClick={handleTranslateBtnClick}
              disabled={isLoading}
              startIcon={<ChatTranslateBold fontSize="small" />}
              data-test-locator="Description Translate"
            >
              {isTranslationShown
                ? T('Show original description')
                : T('Translate description')}
            </Button>
          </Track>
          <Typography.Caption color="muted" className={classes.caption}>
            {T(
              'Automatic translations use Google Translate and may not be completely accurate.',
            )}
          </Typography.Caption>
        </React.Fragment>
      )}
    </div>
  );
};

export { ListingDescription };
